import { useMutation } from '@apollo/client';
import RestartIcon from '@clearkit/icons/glyphs/Restart';
import { CKButton } from '@clearkit/react';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';

import InfoTooltip from '~/components/InfoTooltip';
import MultiPicklist from '~/components/MultiPicklist';
import NestedMultiselect from '~/components/NestedMultiselect';
import TextField from '~/components/RedesignedTextField';
import SlackLogo from '~/images/destinations/Slack.svg';
import { destinationTypeSlack } from '~/lib/destinations';
import { hasError } from '~/lib/graphql';
import { legacySegmentType, readableSingularSegmentType } from '~/lib/segments';
import DeliverTestButton from '~/pages/destinations/components/DeliverTestButton';
import Disconnected from '~/pages/destinations/components/Disconnected';
import SuppressionForm from '~/pages/destinations/components/SuppressionForm';
import TriggerType from '~/pages/destinations/components/TriggerType';
import defaultTriggerTypes from '~/pages/destinations/components/TriggerType/defaultValues';

import ContentBox from '../../components/ContentBox';
import FormContentWrapper from '../../components/FormContentWrapper';
import FormHeader from '../../components/FormHeader';
import sharedStyles from '../../New/styles';
import refreshRemoteMeta from './refreshRemoteMeta.graphql';
import SlackConnectorStatus from './SlackConnectorStatus';
import styles from './styles';

export function combineUsersAndChannels(slackUsers, slackChannels) {
  return slackUsers
    .map((user) => ({
      id: user.id,
      realName: user.profile.realName,
      displayName: user.profile.displayName,
      isChannel: false,
    }))
    .concat(
      slackChannels
        .map(({ id, name }) => ({
          id,
          name,
          options: [],
          isChannel: true,
        }))
        .filter((channel) => !!channel.name),
    );
}

const RefreshButton = ({ connectorId }) => {
  const [refresh, { loading }] = useMutation(refreshRemoteMeta, {
    refetchQueries: ['SegmentDestinationNewPage'],
  });

  return (
    <CKButton
      variant={CKButton.Variant.tertiary}
      variantColor={CKButton.VariantColor.blue}
      disabled={loading}
      onClick={() => {
        refresh({
          variables: {
            input: {
              id: connectorId,
            },
          },
        });
      }}
      leftIcon={<RestartIcon />}>
      {loading ? 'Refreshing' : 'Refresh'}
    </CKButton>
  );
};

class Slack extends React.Component {
  state = {
    columns: [],
    duplicateSuppressionTimeframe: 0,
    triggerType: 'segment_enter',
    channels: [],
    webhookUrl: undefined,
  };

  UNSAFE_componentWillMount() {
    if (this.props.destination) {
      this.setState({
        ...this.props.destination,
        ...this.props.destination.config,
      });
    }
  }

  connectedSlack = () => {
    const { accountConnector } = this.props;

    return accountConnector?.status === 'active';
  };

  handleFieldChange = (name, value) => {
    this.setState({ [name]: value });
  };

  get destinationConfig() {
    const {
      columns,
      duplicateSuppressionTimeframe,
      triggerType,
      channels,
      webhookUrl,
      message,
    } = this.state;

    return {
      config: {
        columns,
        duplicateSuppressionTimeframe,
        webhookUrl: channels ? null : webhookUrl,
        channels,
        message,
      },
      triggerType,
    };
  }

  renderWebhookInput() {
    const { classes, errors } = this.props;
    const { webhookUrl } = this.state;

    return (
      <TextField
        className={classes.field}
        error={hasError(errors, 'webhookUrl')}
        fullWidth
        value={webhookUrl}
        label="Webhook URL"
        disabled
        variant="standard"
      />
    );
  }

  renderChannelSelect = () => {
    const { channels } = this.state;
    const {
      classes,
      errors,
      accountConnector: {
        remoteMeta: { channels: slackChannels = [], users: slackUsers = [] },
        status,
        id,
      },
    } = this.props;

    const channelOptions = combineUsersAndChannels(slackUsers, slackChannels);

    return (
      <FormControl
        error={hasError(errors, 'channels')}
        fullWidth
        required
        className={classes.field}>
        <div className="flex items-center mb-2">
          <FormLabel>Slack Channel</FormLabel>
          <InfoTooltip
            unstable_portal={false}
            title="Choose the channel or channels you would like to send alerts to."
          />
        </div>
        <NestedMultiselect
          menuListIcon={<SlackLogo />}
          menuListActions={<RefreshButton connectorId={id} />}
          disabled={status !== 'active'}
          error={hasError(errors, 'channels')}
          fullWidth
          getOptionLabel={(option) => {
            return option.isChannel
              ? `#${option.name}`
              : `${option.realName} (@${option.displayName ||
                  option.realName})`;
          }}
          getOptionValue={(option) => option.id}
          onChange={(ids) => {
            this.handleFieldChange('channels', ids);
          }}
          options={channelOptions}
          placeholder="Choose a channel"
          required
          title="Channels"
          value={channels}
        />
      </FormControl>
    );
  };

  render() {
    const {
      accountConnector,
      attributes,
      classes,
      errors,
      refetch,
      segment,
      destination,
      FormFooter,
    } = this.props;

    const {
      channels,
      columns,
      webhookUrl,
      message,
      duplicateSuppressionTimeframe,
      triggerType,
    } = this.state;

    if (!this.connectedSlack() && !webhookUrl) {
      return (
        <Disconnected
          service="slack"
          onSuccess={refetch}
          Logo={SlackLogo}
          FormFooter={FormFooter}
        />
      );
    }

    const humanizedSegmentType = readableSingularSegmentType(segment.type);
    const destinationFlowDescription = destination
      ? 'Bot message/channel configured'
      : null;

    return (
      <>
        <FormHeader
          destinationType={destinationTypeSlack}
          destinationName="Slack"
          destinationFlowDescription={destinationFlowDescription}
          segmentName={segment.name}
          segmentCollection={segment.collection.name}
        />
        <FormContentWrapper>
          <h2 className={classes.subheading}>Trigger</h2>
          <span className={classes.triggerSubtext}>
            Choose when this Destination will be run.{' '}
            <a
              href="https://help.clearbit.com/hc/en-us/articles/360023453694"
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'underline' }}>
              Learn more
            </a>
          </span>

          <TriggerType
            errors={errors}
            handleChange={this.handleFieldChange}
            triggerType={triggerType}
            triggerTypes={defaultTriggerTypes(segment.type)}
          />

          <h2 className={classes.subheading}>Configure Slack options</h2>

          <ContentBox>
            {webhookUrl && (
              <SlackConnectorStatus
                accountConnector={accountConnector}
                onSuccess={refetch}
                webhookUrl={webhookUrl}
              />
            )}

            {this.connectedSlack()
              ? this.renderChannelSelect()
              : this.renderWebhookInput()}

            <FormControl
              error={hasError(errors, 'channels')}
              fullWidth
              required
              className={classes.field}>
              <div className="flex items-center mb-2">
                <FormLabel>Custom Message</FormLabel>
                <InfoTooltip
                  unstable_portal={false}
                  title="Include a custom message at the top of your Slack post"
                />
              </div>
              <TextField
                error={hasError(errors, 'message')}
                fullWidth
                defaultValue={message}
                onChange={(event) => {
                  this.handleFieldChange('message', event.target.value);
                }}
                variant="standard"
              />
            </FormControl>
            <hr className={classes.break} />
            <SuppressionForm
              typename={humanizedSegmentType}
              exclusion_timeframe={duplicateSuppressionTimeframe}
              onChange={({ exclusion_timeframe }) => {
                this.handleFieldChange(
                  'duplicateSuppressionTimeframe',
                  exclusion_timeframe,
                );
              }}
            />
          </ContentBox>

          <h2 className={classes.subheading}>Customize your Slack message</h2>

          <p className={classes.columnsHelperText}>
            Select which attributes you want to include as fields in your Slack
            message
          </p>

          <MultiPicklist
            allColumns={attributes}
            onChange={(value) => {
              this.handleFieldChange('columns', value);
            }}
            type={legacySegmentType(segment.type)}
            value={columns || []}
          />

          <h2 className={classes.subheading}>Test this destination</h2>
          <span className={classes.triggerSubtext}>
            Run a test by manually triggering this destination
          </span>
          <ContentBox>
            <DeliverTestButton
              config={{
                columns,
                duplicateSuppressionTimeframe,
                message,
                channels,
                webhookUrl,
              }}
              destinationType="slack"
              label="Slack message"
              segmentId={segment.id}
              segmentType={segment.type}>
              <p>Trigger an example Slack alert</p>
            </DeliverTestButton>
          </ContentBox>
        </FormContentWrapper>
        <FormFooter destinationConfig={this.destinationConfig} />
      </>
    );
  }
}

Slack.propTypes = {
  accountConnector: PropTypes.shape({
    id: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    remoteMeta: PropTypes.shape({
      channels: PropTypes.array.isRequired,
      users: PropTypes.array.isRequired,
    }).isRequired,
  }),
  attributes: PropTypes.array.isRequired,
  classes: PropTypes.object.isRequired,
  destination: PropTypes.object,
  errors: PropTypes.array.isRequired,
  refetch: PropTypes.func.isRequired,
  segment: PropTypes.object.isRequired,
};

export default withStyles(
  (theme) => ({
    ...sharedStyles(theme),
    ...styles(theme),
  }),
  { name: 'SegmentDestinationNewSlack' },
)(Slack);
